/**
 * BankPage — Phase 10 revamped Bank & Cash hub.
 *
 * Tabs:
 *   1. Bank Accounts — list + create + edit + delete (admin/FM scope)
 *   2. Cashbook       — original GL-driven cash movements (preserved)
 *   3. History        — bank-transaction history with filters
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BackToDashBar from "@/components/BackToDashBar";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Banknote, Plus, Loader2, Pencil, Trash2, ShieldAlert, Wallet, ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { fmtUSD } from "@/lib/tx";
import { CashbookPage } from "@/pages/AccountingPages";

const FM_PLUS = new Set([
  ROLES.SYSTEM_ADMIN,
  ROLES.SALIHI_ACCOUNTANT,
  ROLES.SALIHI_SENIOR_ACCOUNTANT,
  ROLES.SALIHI_FINANCE_MANAGER,
]);

const EMPTY = {
  bank_name: "", account_name: "", account_number: "",
  currency: "USD", opening_balance: "0",
  allow_overdraft: false, overdraft_limit: "0",
  status: "active",
};

function fmtDate(s) {
  return s ? (s.length > 10 ? new Date(s).toLocaleDateString() : s) : "—";
}

export default function BankPage() {
  return (
    <div className="space-y-5" data-testid="bank-page">
      <BackToDashBar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Bank &amp; Cash</h2>
          <p className="text-sm text-slate-500 mt-1">
            Bank accounts, cashbook and bank transaction history.
          </p>
        </div>
      </div>

      <Tabs defaultValue="accounts">
        <TabsList data-testid="bank-tabs">
          <TabsTrigger value="accounts" data-testid="bank-tab-accounts">
            <Wallet className="h-4 w-4 mr-2" /> Accounts
          </TabsTrigger>
          <TabsTrigger value="cashbook" data-testid="bank-tab-cashbook">
            <Banknote className="h-4 w-4 mr-2" /> Cashbook
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="bank-tab-history">
            <ArrowRightLeft className="h-4 w-4 mr-2" /> Transaction history
          </TabsTrigger>
        </TabsList>
        <TabsContent value="accounts" className="mt-4">
          <BankAccountsTab />
        </TabsContent>
        <TabsContent value="cashbook" className="mt-4">
          <CashbookPage />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <BankHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BankAccountsTab() {
  const { user, can } = useAuth();
  const role = user?.role;
  // Phase 19 PR-2 hot-fix (2026-07-06) — split create/deposit by their own
  // permission keys. Anything that goes to GL still depends on senior staff.
  const canCreateAccount = can ? can("accounting.bank.create_account") : false;
  const canCreateDeposit = can ? can("accounting.bank.create_deposit") : false;
  const canCreateWithdrawal = can ? can("accounting.bank.create_withdrawal") : false;
  const canWrite = canCreateAccount || canCreateDeposit || canCreateWithdrawal;
  const canDelete = role === ROLES.SYSTEM_ADMIN || user?.full_access === true;
  const canToggleOverdraft = role === ROLES.SYSTEM_ADMIN || user?.full_access === true;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, mode: "create", bank: null });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  // Phase 28 (2026-07-15) — bank-to-bank transfer modal.
  const [showTransfer, setShowTransfer] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = showInactive ? "?include_inactive=true" : "";
      const { data } = await api.get(`/bank-accounts${q}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line

  const openCreate = () => {
    setForm(EMPTY);
    setDlg({ open: true, mode: "create", bank: null });
  };
  const openEdit = (b) => {
    setForm({
      bank_name: b.bank_name, account_name: b.account_name,
      account_number: b.account_number, currency: b.currency,
      opening_balance: String(b.opening_balance ?? 0),
      allow_overdraft: Boolean(b.allow_overdraft),
      overdraft_limit: String(b.overdraft_limit ?? 0),
      status: b.status || "active",
    });
    setDlg({ open: true, mode: "edit", bank: b });
  };

  const submit = async () => {
    if (!form.bank_name || !form.account_name || !form.account_number) {
      toast.error("Bank name, account name and account number are required.");
      return;
    }
    setBusy(true);
    try {
      if (dlg.mode === "create") {
        const payload = {
          bank_name: form.bank_name.trim(),
          account_name: form.account_name.trim(),
          account_number: form.account_number.trim(),
          currency: (form.currency || "USD").toUpperCase(),
          opening_balance: Number(form.opening_balance) || 0,
          allow_overdraft: canToggleOverdraft ? Boolean(form.allow_overdraft) : false,
          overdraft_limit: Number(form.overdraft_limit) || 0,
        };
        await api.post("/bank-accounts", payload);
        toast.success("Bank account created.");
      } else {
        const upd = {
          bank_name: form.bank_name.trim(),
          account_name: form.account_name.trim(),
          account_number: form.account_number.trim(),
          currency: (form.currency || "USD").toUpperCase(),
          overdraft_limit: Number(form.overdraft_limit) || 0,
          status: form.status,
        };
        if (canToggleOverdraft) upd.allow_overdraft = Boolean(form.allow_overdraft);
        await api.patch(`/bank-accounts/${dlg.bank.id}`, upd);
        toast.success("Bank account updated.");
      }
      setDlg({ open: false, mode: "create", bank: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.bank_name} · ${b.account_name}?\n\nIf any transactions are linked, deactivate it instead.`)) return;
    try {
      await api.delete(`/bank-accounts/${b.id}`);
      toast.success("Bank account deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="bank-show-inactive"
            />
            Show inactive
          </label>
        </div>
        {canCreateAccount && (
          <Button
            onClick={() => setShowTransfer(true)}
            variant="outline"
            className="border-[#4FA3D1] text-[#2F86B5] hover:bg-[#4FA3D1]/10"
            data-testid="bank-transfer-btn"
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" /> Transfer
          </Button>
        )}
        {canCreateAccount && (
          <Button
            onClick={openCreate}
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
            data-testid="bank-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Bank Account
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Wallet className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p>No bank accounts yet.</p>
              {canWrite && (
                <p className="text-sm mt-1">Click &ldquo;New bank account&rdquo; to add one.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="bank-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Bank</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>GL Code</TableHead>
                    <TableHead className="text-right">Current Balance</TableHead>
                    <TableHead>Overdraft</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((b) => (
                    <TableRow key={b.id} data-testid={`bank-row-${b.id}`}>
                      <TableCell>
                        <div className="font-medium">{b.bank_name}</div>
                        <div className="text-xs text-slate-500">#{b.account_number}</div>
                      </TableCell>
                      <TableCell>{b.account_name}</TableCell>
                      <TableCell><code className="text-xs">{b.gl_account_code}</code></TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {fmtUSD(b.current_balance)}
                      </TableCell>
                      <TableCell>
                        {b.allow_overdraft ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            Up to {fmtUSD(b.overdraft_limit)}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-500">Blocked</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={b.status === "active"
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                          : "bg-slate-200 text-slate-700 border-slate-300"}>
                          {b.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {canWrite && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(b)}
                            data-testid={`bank-edit-${b.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => remove(b)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`bank-delete-${b.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="bank-dialog">
          <DialogHeader>
            <DialogTitle>
              {dlg.mode === "create" ? "New bank account" : `Edit ${dlg.bank?.bank_name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Bank name *</Label>
              <Input
                value={form.bank_name}
                onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                data-testid="bank-field-bank_name"
              />
            </div>
            <div>
              <Label>Account name *</Label>
              <Input
                value={form.account_name}
                onChange={(e) => setForm((f) => ({ ...f, account_name: e.target.value }))}
                data-testid="bank-field-account_name"
              />
            </div>
            <div>
              <Label>Account number *</Label>
              <Input
                value={form.account_number}
                onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
                data-testid="bank-field-account_number"
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Input
                value={form.currency}
                maxLength={3}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                data-testid="bank-field-currency"
              />
            </div>
            {dlg.mode === "create" && (
              <div>
                <Label>Opening balance</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.opening_balance}
                  onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))}
                  data-testid="bank-field-opening_balance"
                />
              </div>
            )}
            {dlg.mode === "edit" && (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="bank-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow overdraft (admin only)</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    When off, withdrawals that exceed the balance are blocked at creation time.
                  </p>
                </div>
                <Switch
                  checked={Boolean(form.allow_overdraft)}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, allow_overdraft: v }))}
                  disabled={!canToggleOverdraft}
                  data-testid="bank-field-allow_overdraft"
                />
              </div>
            </div>
            {form.allow_overdraft && (
              <div className="sm:col-span-2">
                <Label>Overdraft limit (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.overdraft_limit}
                  onChange={(e) => setForm((f) => ({ ...f, overdraft_limit: e.target.value }))}
                  data-testid="bank-field-overdraft_limit"
                />
              </div>
            )}
          </div>
          {dlg.mode === "create" && (
            <p className="text-xs text-slate-500">
              A GL sub-account in the 1010-series will be auto-created and linked
              to this bank. The opening balance only affects the General Ledger
              after the corresponding &ldquo;Beginning Bank Balance&rdquo; transaction is
              posted through the standard approval workflow.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, mode: "create", bank: null })} disabled={busy}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
              data-testid="bank-save-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {dlg.mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TransferDialog
        open={showTransfer}
        onOpenChange={setShowTransfer}
        banks={items}
        onDone={() => { setShowTransfer(false); load(); toast.info("Refresh the Dashboard to see updated bank balances."); }}
      />
    </div>
  );
}

function TransferDialog({ open, onOpenChange, banks, onDone }) {
  // Phase 28 (2026-07-15) — bank-to-bank transfer modal.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!from || !to) { toast.error("Pick both banks"); return; }
    if (from === to) { toast.error("Source and destination must differ"); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be positive"); return; }
    setBusy(true);
    try {
      const r = await api.post("/bank/transfer", {
        from_bank_id: from, to_bank_id: to, amount: amt,
        transfer_date: date, reference: ref,
      });
      toast.success(`Transferred $${amt.toLocaleString()} — new src balance $${r.data.from_bank.new_balance.toLocaleString()}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bank-transfer-modal">
        <DialogHeader><DialogTitle>Bank-to-bank transfer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-1">
            <Label className="text-xs">From</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source bank" /></SelectTrigger>
              <SelectContent>
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.bank_name} · {fmtUSD(b.balance ?? 0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label className="text-xs">To</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination bank" /></SelectTrigger>
              <SelectContent>
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.bank_name} · {fmtUSD(b.balance ?? 0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Amount (USD)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="transfer-amount" />
          </div>
          <div className="col-span-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="transfer-date" />
            {/* Phase 52-C (2026-08-06) — parse-preview under the input so
                users spot mm/dd vs dd/mm ambiguity before submit. */}
            <p className="text-[10px] text-slate-500 mt-0.5" data-testid="transfer-date-preview">
              {date ? `Will post as: ${new Date(date + "T00:00:00").toLocaleDateString("en-GB", {day:"numeric",month:"long",year:"numeric"})}` : "—"}
            </p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Reference</Label>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="ZA-ZM-001" data-testid="transfer-ref" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="bank-transfer-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Bug bundle #16 (2026-08-19) — Transaction history rebuilt on top of
// /api/bank/ledger so EVERY JE line touching a cash/bank account shows up
// (payroll pay-out, expense payments, loan drawdowns, transfers, refunds,
// PAYE remittances, bank fees, manual adjustments). Legacy behaviour only
// listed rows in the `transactions` collection whose `transaction_type`
// was `bank_transfer_*`, which excluded 95 % of real movements.
// Bug bundle #18 (2026-08-19) — column → row-field map for the sortable
// Transaction history table. `date` is the only sort under which the
// "Balance After" column is meaningful (chronological order).
const SORT_FIELD = {
  date: "entry_date",
  bank: "account_code",
  source: "source_type",
  direction: "direction",
  amount: "amount",
  balance: "balance_after",
};

function fmtSigned(v) {
  const n = Number(v || 0);
  if (n < 0) {
    return `($${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
  }
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BankHistoryTab() {
  const [cashAccounts, setCashAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterActive, setFilterActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedJe, setSelectedJe] = useState(null);
  // Bug bundle #18 (2026-08-19) — client-side sort state. `by="date"` keeps
  // the chronological order the backend already returns and lets the
  // "Balance After" column render meaningfully. Any other sort renders the
  // column dimmed with a tooltip explaining why.
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");
  const [filters, setFilters] = useState({
    account_code: "",
    date_from: "",
    date_to: "",
    source_types: [],
    direction: "",
  });

  const SOURCE_OPTIONS = [
    { key: "payroll",    label: "Payroll" },
    { key: "expense",    label: "Expense" },
    { key: "income",     label: "Income" },
    { key: "loan",       label: "Loan" },
    { key: "transfer",   label: "Transfer" },
    { key: "refund",     label: "Refund" },
    { key: "bank_fee",   label: "Bank fee" },
    { key: "adjustment", label: "Adjustment" },
    { key: "manual",     label: "Manual" },
  ];
  const SOURCE_STYLE = {
    payroll:    "bg-purple-100 text-purple-800 border-purple-200",
    expense:    "bg-rose-100 text-rose-800 border-rose-200",
    income:     "bg-emerald-100 text-emerald-800 border-emerald-200",
    loan:       "bg-amber-100 text-amber-800 border-amber-200",
    transfer:   "bg-sky-100 text-sky-800 border-sky-200",
    refund:     "bg-blue-100 text-blue-800 border-blue-200",
    bank_fee:   "bg-slate-100 text-slate-800 border-slate-200",
    adjustment: "bg-orange-100 text-orange-800 border-orange-200",
    manual:     "bg-slate-100 text-slate-700 border-slate-200",
  };

  useEffect(() => {
    api.get("/bank/cash-accounts").then(({ data }) => {
      const accs = (data?.accounts || [])
        .filter((a) => Math.abs(a.balance || 0) > 0.001 || a.is_bank_subaccount)
        .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      setCashAccounts(accs);
    }).catch(() => setCashAccounts([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.account_code) params.account_code = filters.account_code;
      else params.all_cash = "true";
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.source_types.length) params.source_types = filters.source_types.join(",");
      if (filters.direction) params.direction = filters.direction;
      const { data } = await api.get("/bank/ledger", { params });
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
      setFilterActive(!!data?.filter_active);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleSource = (key) => {
    setFilters((f) => ({
      ...f,
      source_types: f.source_types.includes(key)
        ? f.source_types.filter((k) => k !== key)
        : [...f.source_types, key],
    }));
  };

  const exportCsv = () => {
    const header = ["Date", "Bank", "Source", "Direction", "Narration", "Counterparty", "Amount", "Debit", "Credit", "Balance After", "Hidden Before", "JE Reference"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of sortedRows) {
      lines.push([
        r.entry_date,
        `${r.account_code} ${r.account_name}`,
        r.source_type,
        r.direction,
        r.narration,
        r.counterparty_name,
        r.amount,
        r.debit,
        r.credit,
        r.balance_after ?? r.running_balance,
        r.hidden_before || 0,
        r.je_reference || r.je_id,
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `bank-ledger-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Bug bundle #18 — apply client-side sorting on top of the chronological
  // rows the backend returned. Whenever the user re-sorts away from Date,
  // the "Balance After" column dims (see column header + cell rendering).
  const sortedRows = (() => {
    const arr = rows.slice();
    const cmp = (a, b) => {
      const sign = sortDir === "asc" ? 1 : -1;
      const va = (a || {})[SORT_FIELD[sortBy]];
      const vb = (b || {})[SORT_FIELD[sortBy]];
      if (va == null && vb == null) return 0;
      if (va == null) return 1 * sign;
      if (vb == null) return -1 * sign;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
      return String(va).localeCompare(String(vb)) * sign;
    };
    return arr.sort(cmp);
  })();

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir(key === "date" ? "asc" : "desc"); }
  };

  const balanceAfterMeaningful = sortBy === "date";

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-5">
            <div>
              <Label className="text-xs">Bank / cash account</Label>
              <Select
                value={filters.account_code || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, account_code: v === "all" ? "" : v }))}
              >
                <SelectTrigger data-testid="history-account-filter" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cash + bank</SelectItem>
                  {cashAccounts.map((a) => (
                    <SelectItem key={a.code} value={a.code}>
                      {a.code} · {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.date_from} className="h-9"
                     onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                     data-testid="history-date-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.date_to} className="h-9"
                     onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                     data-testid="history-date-to" />
            </div>
            <div>
              <Label className="text-xs">Direction</Label>
              <Select value={filters.direction || "all"}
                      onValueChange={(v) => setFilters((f) => ({ ...f, direction: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="history-direction-filter" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All directions</SelectItem>
                  <SelectItem value="in">Money in</SelectItem>
                  <SelectItem value="out">Money out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={load} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white flex-1 h-9"
                      data-testid="history-apply">Apply</Button>
              <Button onClick={exportCsv} variant="outline" className="h-9"
                      data-testid="history-export-csv" disabled={rows.length === 0}>
                CSV
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Source type</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="history-source-chips">
              {SOURCE_OPTIONS.map((o) => {
                const on = filters.source_types.includes(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleSource(o.key)}
                    className={`text-[11px] px-2 py-0.5 rounded border transition ${
                      on ? SOURCE_STYLE[o.key] : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`history-source-chip-${o.key}`}
                  >
                    {o.label}
                  </button>
                );
              })}
              {filters.source_types.length > 0 && (
                <button type="button"
                        onClick={() => setFilters((f) => ({ ...f, source_types: [] }))}
                        className="text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:text-rose-500 hover:border-rose-300"
                        data-testid="history-source-chip-clear">
                  Clear
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="history-table">
                <TableHeader>
                  <TableRow>
                    {[
                      { key: "date",      label: "Date",       align: "" },
                      { key: "bank",      label: "Bank",       align: "" },
                      { key: "source",    label: "Source",     align: "" },
                      { key: "direction", label: "Dir.",       align: "" },
                    ].map((c) => (
                      <TableHead key={c.key} onClick={() => toggleSort(c.key)}
                                 className="cursor-pointer select-none">
                        {c.label}
                        {sortBy === c.key && (
                          <span className="text-[10px] ml-1 opacity-60">
                            {sortDir === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </TableHead>
                    ))}
                    <TableHead>Narration</TableHead>
                    <TableHead>Counterparty</TableHead>
                    <TableHead className="text-right cursor-pointer select-none"
                               onClick={() => toggleSort("amount")}>
                      Amount
                      {sortBy === "amount" && (
                        <span className="text-[10px] ml-1 opacity-60">
                          {sortDir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </TableHead>
                    <TableHead
                      className={`text-right cursor-pointer select-none ${!balanceAfterMeaningful ? "opacity-40" : ""}`}
                      onClick={() => toggleSort("balance")}
                      title={!balanceAfterMeaningful
                        ? "Balance After only accurate when sorted by Date"
                        : "Bank balance immediately after each posting"}
                      data-testid="history-col-balance-after"
                    >
                      Balance After
                      {sortBy === "balance" && (
                        <span className="text-[10px] ml-1 opacity-60">
                          {sortDir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </TableHead>
                    <TableHead>JE Ref</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No transactions match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedRows.map((r, i) => (
                    <TableRow key={`${r.je_id}-${i}`} data-testid={`history-row-${i}`}>
                      <TableCell className="whitespace-nowrap">
                        {fmtDate(r.entry_date)}
                        {filterActive && r.hidden_before > 0 && balanceAfterMeaningful && (
                          <div
                            className="text-[10px] text-slate-400 mt-0.5"
                            title={`Filter excluded ${r.hidden_before} row(s) between this and the previous visible movement for ${r.account_code}. The Balance After still reflects the true chronological ledger.`}
                            data-testid={`history-row-hidden-hint-${i}`}
                          >
                            excludes {r.hidden_before} filtered row{r.hidden_before === 1 ? "" : "s"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="font-mono">{r.account_code}</span> {r.account_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${SOURCE_STYLE[r.source_type] || SOURCE_STYLE.manual}`}>
                          {r.source_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${
                          r.direction === "in"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}>
                          {r.direction === "in" ? "In" : "Out"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate">{r.narration}</TableCell>
                      <TableCell className="max-w-[180px] truncate text-slate-600">{r.counterparty_name || "—"}</TableCell>
                      <TableCell className={`text-right tabular-nums ${r.direction === "out" ? "text-rose-700" : "text-emerald-700"}`}>
                        {fmtUSD(r.amount)}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums font-medium ${
                          !balanceAfterMeaningful
                            ? "opacity-40"
                            : (Number(r.balance_after ?? r.running_balance) < 0 ? "text-rose-700" : "text-slate-800")
                        }`}
                        data-testid={`history-row-balance-${i}`}
                        title={!balanceAfterMeaningful
                          ? "Balance After only accurate when sorted by Date"
                          : `${r.account_code} balance after this posting`}
                      >
                        {fmtSigned(r.balance_after ?? r.running_balance)}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setSelectedJe(r.je_id)}
                          className="text-xs text-[#4FA3D1] hover:underline font-mono"
                          data-testid={`history-je-link-${i}`}
                        >
                          {r.je_reference || r.je_id?.slice(0, 8)}
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {sortedRows.length > 0 && (
                <div className="p-3 text-xs text-slate-500 border-t flex items-center justify-between">
                  <span>Showing {sortedRows.length} of {total} row(s)</span>
                  {!balanceAfterMeaningful && (
                    <span className="text-amber-600" data-testid="history-balance-warning">
                      Balance After column is dimmed — sort by Date to see chronological running balance
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedJe && (
        <BankLedgerJEDialog jeId={selectedJe} onClose={() => setSelectedJe(null)} />
      )}
    </div>
  );
}

// Compact JE drill-through for the Bank Ledger table.
function BankLedgerJEDialog({ jeId, onClose }) {
  const [je, setJe] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/journal-entries/${jeId}`);
        setJe(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [jeId]);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bank-ledger-je-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Journal Entry</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
        ) : je ? (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-slate-500">Reference:</span> <span className="font-mono">{je.reference || "—"}</span></div>
              <div><span className="text-slate-500">Date:</span> {fmtDate(je.entry_date)}</div>
              <div className="col-span-2"><span className="text-slate-500">Narration:</span> {je.narration}</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(je.lines || []).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.account_code} — {l.account_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(l.debit) > 0 ? fmtUSD(l.debit) : ""}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(l.credit) > 0 ? fmtUSD(l.credit) : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-slate-500 py-8 text-center">JE not found.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bank-ledger-je-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
